import React, { useState, useEffect } from 'react';
import { getStatistics } from '../services/api';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#0969da', '#2da44e', '#8250df', '#cf222e', '#bf8700', '#6e7781'];
const RARITY_COLORS = {
  'common': '#6e7781',
  'uncommon': '#2da44e',
  'rare': '#bf8700',
  'very_rare': '#d4a72c',
  'extremely_rare': '#cf222e'
};

const Dashboard = ({ themeClasses, isDarkMode }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await getStatistics();
        setStats(data.statistics);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  if (loading) return <div className={`flex items-center justify-center p-12 ${themeClasses.textMuted}`}>Loading dashboard...</div>;
  if (!stats) return <div className="text-red-500 p-12 text-center">Unable to load statistics.</div>;

  // Prepare Pie Chart data (Rarity)
  const rarityData = stats.rarity_distribution 
    ? stats.rarity_distribution.map(item => ({ name: item.rarity, value: parseInt(item.count) })) 
    : [];

  return (
    <div className="w-full h-full">
      {/* 1. KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className={`${themeClasses.card} p-6 rounded-lg border shadow-sm`}>
          <span className={`text-xs font-bold tracking-widest uppercase ${themeClasses.textMuted} mb-2 block`}>Total Items</span>
          <span className={`text-3xl font-light ${themeClasses.textMain}`}>{stats.total_items}</span>
        </div>
        <div className={`${themeClasses.card} p-6 rounded-lg border shadow-sm`}>
          <span className={`text-xs font-bold tracking-widest uppercase ${themeClasses.textMuted} mb-2 block`}>Coins</span>
          <span className={`text-3xl font-light ${themeClasses.textMain}`}>{stats.total_coins}</span>
        </div>
        <div className={`${themeClasses.card} p-6 rounded-lg border shadow-sm`}>
          <span className={`text-xs font-bold tracking-widest uppercase ${themeClasses.textMuted} mb-2 block`}>Banknotes</span>
          <span className={`text-3xl font-light ${themeClasses.textMain}`}>{stats.total_banknotes}</span>
        </div>
        <div className={`${themeClasses.card} p-6 rounded-lg border shadow-sm border-l-4 ${isDarkMode ? 'border-l-[#4FD1C5]' : 'border-l-[#2D6A4F]'}`}>
          <span className={`text-xs font-bold tracking-widest uppercase ${themeClasses.textMuted} mb-2 block`}>Total Value</span>
          <span className={`text-3xl font-light ${themeClasses.textMain}`}>€{stats.total_value ? Number(stats.total_value).toFixed(2) : '0.00'}</span>
        </div>
      </div>

      {/* 2. Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        
        {/* Top Countries Bar Chart */}
        <div className={`${themeClasses.card} p-6 rounded-lg border shadow-sm`}>
          <h3 className={`text-xs font-bold tracking-widest uppercase mb-6 ${themeClasses.textMain}`}>Top Countries</h3>
          <div style={{ width: '100%', height: 300 }}>
            {stats.top_countries && stats.top_countries.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={stats.top_countries} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="country" width={100} tick={{ fontSize: 12, fill: isDarkMode ? '#94a3b8' : '#475569' }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', backgroundColor: isDarkMode ? '#1e293b' : '#fff' }}
                  />
                  <Bar dataKey="count" fill="#0969da" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
                <div className={`text-center py-20 ${themeClasses.textMuted}`}>Not enough data</div>
            )}
          </div>
        </div>

        {/* Rarity Pie Chart */}
        <div className={`${themeClasses.card} p-6 rounded-lg border shadow-sm`}>
          <h3 className={`text-xs font-bold tracking-widest uppercase mb-6 ${themeClasses.textMain}`}>Rarity Distribution</h3>
          <div style={{ width: '100%', height: 300 }}>
             {rarityData.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={rarityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {rarityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={RARITY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', backgroundColor: isDarkMode ? '#1e293b' : '#fff' }}
                  />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
             ) : (
                 <div className={`text-center py-20 ${themeClasses.textMuted}`}>Not enough data</div>
             )}
          </div>
        </div>
        
      </div>

      {/* 3. AI Insights (Placeholder for future) */}
      <div className={`p-6 rounded-lg border shadow-sm bg-gradient-to-r relative overflow-hidden ${isDarkMode ? 'from-indigo-900/40 to-purple-900/40 border-indigo-500/30' : 'from-indigo-50 to-purple-50 border-indigo-100'}`}>
          <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">✨</span>
              <h4 className={`font-bold ${themeClasses.textMain}`}>AI Curator Insight</h4>
          </div>
          <p className={`${themeClasses.textMuted} mb-4 max-w-2xl`}>
            Your collection looks great! You have a strong focus on <strong className={themeClasses.textMain}>{stats.top_countries?.[0]?.country || 'Unknown'}</strong>. 
            Consider looking for missing years to complete your series.
          </p>
          <button className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded border ${isDarkMode ? 'border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'} transition-colors`}>
            Generate Full Report
          </button>
      </div>

    </div>
  );
};

export default Dashboard;

